import { Box, Tab, Tabs } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { PropsWithChildren, ReactElement, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePaloHeadSlot } from '@synergy/components-palo';

export interface PaloConfigurationTab {
    value: string;
    /** Translation id of the tab label. */
    label: string;
    /** Query string this tab needs, e.g. its own sort order. */
    searchParams?: string;
}

export interface PaloConfigurationShellProps {
    /** Translation id of the screen title. */
    title: string;
    tabs: PaloConfigurationTab[];
    /** Right-aligned header slot: search, language switch, create menu. */
    actions?: ReactNode;
    /** Hides the tab row, e.g. while a detail owns the screen. */
    hideTabs?: boolean;
}

/**
 * PALO chrome for the configuration screens: canvas, title, header actions and
 * the tab row. The three configuration areas share this shape, so they differ
 * only in their tabs, actions and routed content.
 */
export const PaloConfigurationShell = ({
    title,
    tabs,
    actions,
    hideTabs,
    children,
}: PropsWithChildren<PaloConfigurationShellProps>): ReactElement => {
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();

    // Without this the browser tab keeps whatever record was open before
    // (#2212, R-22).
    useEffect(() => {
        document.title = intl.formatMessage({ id: title });
    }, [intl, title]);

    const activeTab = tabs.find(tab => location.pathname.includes(tab.value))?.value ?? tabs[0]?.value;

    // The configuration screens name themselves in the top bar like every
    // other page; the bar used to say "Stammdaten" over a screen whose own
    // heading, one row below, read "Suchqualität" (06.10.2026).
    const headSlot = usePaloHeadSlot(true);
    const inHeadSlot = null !== headSlot.element;

    const headRow = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: inHeadSlot ? '10px' : '14px',
                ...(inHeadSlot ? { width: '100%', minWidth: 0, flexWrap: 'nowrap' } : { mb: '11px', flexWrap: 'wrap' }),
            }}
        >
            <Box
                sx={{
                    fontSize: inHeadSlot ? '14px' : '17px',
                    fontWeight: 640,
                    letterSpacing: '-.015em',
                    color: tokens.color.ink,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                <FormattedMessage id={title} />
            </Box>
            {actions && (
                <Box
                    sx={{
                        marginLeft: 'auto',
                        display: 'flex',
                        gap: '7px',
                        alignItems: 'center',
                        flexShrink: 0,
                        flexWrap: inHeadSlot ? 'nowrap' : 'wrap',
                    }}
                >
                    {actions}
                </Box>
            )}
        </Box>
    );

    return (
        <PaloThemeProvider>
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: tokens.color.canvas,
                }}
            >
                <Box
                    sx={{
                        backgroundColor: tokens.color.surface,
                        borderBottom: `1px solid ${tokens.color.line}`,
                        padding: inHeadSlot ? '0 16px' : '11px 16px 0',
                        flexShrink: 0,
                    }}
                >
                    {inHeadSlot ? createPortal(headRow, headSlot.element as HTMLElement) : headRow}
                    {!hideTabs && (
                        <Tabs
                            value={activeTab}
                            onChange={(_event, value) => {
                                const tab = tabs.find(entry => entry.value === value);
                                navigate({ pathname: value, search: tab?.searchParams ?? location.search });
                            }}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {tabs.map(tab => (
                                <Tab key={tab.value} value={tab.value} label={<FormattedMessage id={tab.label} />} />
                            ))}
                        </Tabs>
                    )}
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>{children}</Box>
            </Box>
        </PaloThemeProvider>
    );
};
